import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { CookieNotice } from "@/components/marketing/CookieNotice";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import Signup from "@/pages/Signup";
import Legal from "@/pages/Legal";
import Contact from "@/pages/Contact";
import Tour from "@/pages/Tour";
import Enterprise from "@/pages/Enterprise";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import { Portfolios, Programmes } from "@/pages/Hierarchy";
import { AdminLists, AdminUsers, AdminBranding } from "@/pages/Admin";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminApprovers from "@/pages/AdminApprovers";
import Reports from "@/pages/Reports";
import Resources from "@/pages/Resources";
import Assistant from "@/pages/Assistant";
import Controls from "@/pages/Controls";
import AdminPeriods from "@/pages/AdminPeriods";
import Approvals from "@/pages/Approvals";
import ProgrammeReport from "@/pages/ProgrammeReport";
import Clients from "@/pages/Clients";
import ClientReport from "@/pages/ClientReport";

import AdminBI from "@/pages/AdminBI";
import AdminScheduleTemplates from "@/pages/AdminScheduleTemplates";
import AdminFrameworks from "@/pages/AdminFrameworks";
import Compliance from "@/pages/Compliance";
import AdminSector from "@/pages/AdminSector";
import AdminDemoData from "@/pages/AdminDemoData";
import AdminBilling from "@/pages/AdminBilling";
import PaymentResult from "@/pages/PaymentResult";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminAI from "@/pages/AdminAI";
import AdminAIEngine from "@/pages/AdminAIEngine";
import AdminAssurance from "@/pages/AdminAssurance";
import Support from "@/pages/Support";
import PlatformConsole from "@/pages/PlatformConsole";
import PlatformTickets from "@/pages/PlatformTickets";

const Protected = ({ children, roles }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>
    );
  // Carry where they were going. Without this, anyone arriving with a purchase intent (or any deep
  // link) on an expired session lands on a bare /login and their intent is silently discarded.
  if (user === false) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (user.role === "sponsor") return <Navigate to="/client" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const Landing = () => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (!user) return <Home />;
  return <Navigate to={user.role === "sponsor" ? "/client" : "/dashboard"} replace />;
};

const PlatformOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  // The server is the real gate (it 404s a non-owner); this only keeps the page out of the UI.
  if (!user.platform_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

const ClientView = () => {  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <ClientDashboard />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <EntitlementsProvider>
          <Toaster position="top-right" richColors />
          <CookieNotice />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/tour" element={<Tour />} />
            <Route path="/enterprise" element={<Enterprise />} />

            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/privacy" element={<Legal />} />
            <Route path="/terms" element={<Legal />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/portfolios" element={<Protected><Portfolios /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id/report" element={<Protected><ClientReport /></Protected>} />
            <Route path="/programmes" element={<Protected><Programmes /></Protected>} />
            <Route path="/programmes/:id/report" element={<Protected><ProgrammeReport /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
            <Route path="/resources" element={<Protected><Resources /></Protected>} />
            <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
            <Route path="/controls/:kind" element={<Protected><Controls /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin", "pmo"]}><AdminLists /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["admin", "pmo"]}><AdminUsers /></Protected>} />
            <Route path="/admin/branding" element={<Protected roles={["admin", "pmo"]}><AdminBranding /></Protected>} />
            <Route path="/admin/approvers" element={<Protected roles={["admin", "pmo"]}><AdminApprovers /></Protected>} />
            <Route path="/admin/periods" element={<Protected roles={["admin", "pmo"]}><AdminPeriods /></Protected>} />
            <Route path="/admin/bi" element={<Protected roles={["admin", "pmo"]}><AdminBI /></Protected>} />
            <Route path="/admin/schedule-templates" element={<Protected roles={["admin", "pmo"]}><AdminScheduleTemplates /></Protected>} />
            <Route path="/admin/frameworks" element={<Protected roles={["admin", "pmo"]}><AdminFrameworks /></Protected>} />
            <Route path="/admin/sector" element={<Protected roles={["admin", "pmo"]}><AdminSector /></Protected>} />
            <Route path="/admin/demo-data" element={<Protected roles={["admin", "pmo"]}><AdminDemoData /></Protected>} />
            <Route path="/admin/notifications" element={<Protected roles={["admin", "pmo"]}><AdminNotifications /></Protected>} />
            <Route path="/admin/billing" element={<Protected roles={["admin", "pmo"]}><AdminBilling /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentResult cancelled /></Protected>} />
            <Route path="/admin/ai-engine" element={<Protected roles={["admin", "pmo"]}><AdminAIEngine /></Protected>} />
            <Route path="/admin/ai-assurance" element={<Protected roles={["admin", "pmo"]}><AdminAssurance /></Protected>} />
            <Route path="/admin/ai" element={<Protected roles={["admin", "pmo"]}><AdminAI /></Protected>} />
            <Route path="/support" element={<Protected><Support /></Protected>} />
            <Route path="/platform" element={<PlatformOnly><PlatformConsole /></PlatformOnly>} />
            <Route path="/platform/tickets" element={<PlatformOnly><PlatformTickets /></PlatformOnly>} />
            <Route path="/client" element={<ClientView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </EntitlementsProvider>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
